import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PROCESS_STEPS, PROCESS_TRAITS } from "@/data/content";
import { Reveal, ChapterBadge } from "@/components/Reveal";

export default function Process() {
  const [active, setActive] = useState(0);

  return (
    <section data-testid="process-section" className="py-20 sm:py-28 bg-charcoal-900 text-parchment overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ChapterBadge number="08" label="Operational Engine" light />
        <Reveal className="mt-8 max-w-2xl">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.1]">
            Brief to publish — <em className="italic text-copper-500">a working system.</em>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-parchment/60 leading-relaxed">
            Creative operations is a discipline, not a department. This is the end-to-end workflow
            behind every deliverable.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {PROCESS_STEPS.map((step, i) => {
            const isActive = active === i;
            return (
              <Reveal key={step.id} delay={Math.min(i * 0.05, 0.3)}>
                <button
                  data-testid={`process-step-${step.id}`}
                  onClick={() => setActive(i)}
                  aria-pressed={isActive}
                  className={`w-full rounded-xl border p-4 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 ${
                    isActive
                      ? "border-copper-500 bg-copper-500/10"
                      : "border-parchment/15 bg-parchment/[0.03] hover:border-parchment/35"
                  }`}
                >
                  <span className={`block text-sm font-semibold ${isActive ? "text-parchment" : "text-parchment/75"}`}>
                    {step.label}
                  </span>
                  <motion.span
                    className="mt-3 block h-0.5 rounded-full bg-copper-500"
                    initial={false}
                    animate={{ width: isActive ? "100%" : "22%" }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  />
                </button>
              </Reveal>
            );
          })}
        </div>

        <div className="mt-8 min-h-[96px] rounded-2xl border border-parchment/10 bg-parchment/[0.03] p-6 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-copper-500">
                {PROCESS_STEPS[active].label}
              </p>
              <p data-testid="process-step-detail" className="mt-2 text-base sm:text-lg text-parchment/85 leading-relaxed max-w-3xl">
                {PROCESS_STEPS[active].text}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        <Reveal className="mt-10">
          <div className="flex flex-wrap gap-2">
            {PROCESS_TRAITS.map((trait) => (
              <span
                key={trait}
                className="text-xs text-parchment/60 border border-parchment/15 rounded-full px-3 py-1.5"
              >
                {trait}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
